import { TFunction } from "i18next";

import { PermissionsRow } from "components/TableDetails/UserDetails/Permissions/types";
import { UserWithCompanyAttributes } from "context/auth/types";
import {
    doesAccessLevelAllowToManageUser,
    FILE_TYPES,
    ID_TYPE,
    isAllowedToEditCompany,
    isUserHimself,
    NOT_FILTER_NAMES,
    QueryParamName,
    QueryParams,
    ROLE_TYPES,
    TABLE_NAMES,
    TASK_STATUSES,
    TELTONIKA_FILES_COMPANY,
    TRUE,
    USER_ACCESS_LEVELS
} from "shared";

import { TableInitialState } from "../types";

export const getActiveQueryParams = (
    isEmpty: boolean,
    state: TableInitialState,
    queryParams: QueryParams
) => {
    if (isEmpty) return {};

    const allQueryParams = {
        ...state.innerTableQueryParams,
        ...queryParams
    };

    Object.entries(allQueryParams).forEach(([key, value]) => {
        if (value === null || value.length === 0) {
            delete allQueryParams[key as QueryParamName];
        }
    });

    return allQueryParams;
};

export const updateSelectedRows = (state: TableInitialState, rowId: any) => {
    const rowIndex = state.selectedRows.findIndex(
        selectedRow => selectedRow === rowId
    );

    if (rowIndex === -1) {
        return [...state.selectedRows, rowId];
    } else {
        return state.selectedRows.filter(selectedRow => selectedRow !== rowId);
    }
};

export const deleteUpdatedQueryParams = (
    initialParams: QueryParamName[],
    key: QueryParamName,
    isFilter: (paramName: string) => boolean,
    updatedQueryParams: QueryParams
) => {
    if (
        (!initialParams.includes(key) && isFilter(key)) ||
        key === NOT_FILTER_NAMES.Query
    ) {
        delete updatedQueryParams[key];
    }
};

export const getUpdatedRowsAfterSingleRowUpdate = (
    rows: any[],
    idType: ID_TYPE,
    itemId: number,
    newData: any
) => rows.map(row => (row[idType] === itemId ? newData : row));

export const getSelectableRows = (
    name: TABLE_NAMES,
    isNotSelectable: boolean,
    rows: any[],
    user: UserWithCompanyAttributes | null
) => {
    if (isNotSelectable) {
        return [];
    }

    if (name === TABLE_NAMES.Tasks) {
        return rows.filter(
            ({ status_id }: { status_id: TASK_STATUSES }) =>
                status_id === TASK_STATUSES.Pending ||
                status_id === TASK_STATUSES.Running
        );
    }

    if (name === TABLE_NAMES.Files) {
        const isAdminManagerUser =
            user!.access_level >= USER_ACCESS_LEVELS.AdminManager;

        const canDeletefile = (type: FILE_TYPES) => {
            switch (type) {
                case FILE_TYPES.Dtb:
                    return isAdminManagerUser;
                case FILE_TYPES.BundleBleFw:
                case FILE_TYPES.BundleCanFw:
                case FILE_TYPES.BundleMainDtb:
                case FILE_TYPES.BundleMainFw:
                    return false;
                default:
                    return true;
            }
        };

        return rows.filter(
            ({ company_id, type }: any) =>
                company_id !== TELTONIKA_FILES_COMPANY && canDeletefile(type)
        );
    }

    if (name === TABLE_NAMES.Companies) {
        return rows.filter((row: any) => {
            const { id, type, code } = row;
            const isNotUserCompany = user?.company_id !== id;
            const userAccessLevel = user!.access_level;

            return (
                isNotUserCompany &&
                isAllowedToEditCompany(userAccessLevel, type, code)
            );
        });
    }

    if (name === TABLE_NAMES.Users) {
        return rows.filter(
            ({ id, access_level }: any) =>
                !isUserHimself(user!.id, id) &&
                doesAccessLevelAllowToManageUser(user!, access_level)
        );
    }

    if (name === TABLE_NAMES.Roles) {
        return rows.filter(({ type }: any) => type !== ROLE_TYPES.Static);
    }

    return rows;
};

export const getUpdatedSelectedRows = (
    currentSelectedRows: number[],
    selectableRows: any[],
    idType: ID_TYPE
) => {
    const selectedRowsCopy = [...currentSelectedRows];

    return selectedRowsCopy.filter(rowId => {
        return selectableRows.some(
            selectableRow => selectableRow[idType] === rowId
        );
    });
};

export const getTotalRows = (totalRows: number, imeiArrayLength: number) => {
    const rowDifference = totalRows - imeiArrayLength;
    const rowCount = rowDifference > 0 ? rowDifference : 0;

    return totalRows === 0 ? 0 : rowCount;
};

export const getUpdatedRowsAfterMultipleRowsDelete = (
    stateRows: any[],
    idArray: number[],
    idType: ID_TYPE
) => {
    const currentRows = [...stateRows];

    return currentRows.filter(row => {
        const imeiSet = new Set(idArray);

        return !imeiSet.has(row[idType]);
    });
};

const getLocallyFilteredRows = (
    tableName: TABLE_NAMES,
    queryParams: QueryParams,
    initialRows: any[],
    t: TFunction<"translation", undefined>
) => {
    const { query, status, allPermissions } = queryParams;

    if (tableName === TABLE_NAMES.Permissions) {
        return initialRows.filter((row: PermissionsRow) => {
            const { id, name, roleStatuses } = row;

            // Search
            let matchesQuery = true;

            if (query) {
                const translatedName = t(`General##${name}`);

                matchesQuery = translatedName
                    .toLowerCase()
                    .includes((query as string).toLowerCase());
            }

            // Status filter
            let matchesStatus = true;

            if (status) {
                const isEnabled = status === TRUE;
                const roleStatusesValues = Object.values(roleStatuses);

                matchesStatus = isEnabled
                    ? roleStatusesValues.some(Boolean)
                    : roleStatusesValues.every(val => !val);
            }

            // All permissions
            let matchesAllPermissions = true;

            if (allPermissions) {
                matchesAllPermissions = allPermissions.includes(id);
            }

            return matchesQuery && matchesStatus && matchesAllPermissions;
        });
    }

    return initialRows;
};

export const setRows = (
    hasLocalFiltering: boolean,
    tableName: TABLE_NAMES,
    queryParams: QueryParams,
    initialRows: any,
    currentRows: any,
    t: TFunction<"translation", undefined>
) =>
    hasLocalFiltering
        ? getLocallyFilteredRows(tableName, queryParams, initialRows, t)
        : currentRows;
